import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';

export type TableSortDirection = 'ascending' | 'descending';

export type TableSortState = {
  key: string;
  direction: TableSortDirection;
} | null;

const compareValues = (left: unknown, right: unknown): number => {
  if (left == null || right == null) {
    if (left == null && right == null) return 0;
    return left == null ? 1 : -1;
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
};

export const sortTableRows = <T,>(
  rows: readonly T[],
  sort: TableSortState,
  getValue: (row: T, key: string) => unknown
): T[] => {
  if (!sort) return [...rows];
  return rows
    .map((row, index) => ({ row, index, value: getValue(row, sort.key) }))
    .sort((left, right) => {
      const comparison = compareValues(left.value, right.value);
      if (comparison === 0) return left.index - right.index;
      if (left.value == null || right.value == null) return comparison;
      return sort.direction === 'ascending' ? comparison : -comparison;
    })
    .map(({ row }) => row);
};

export const useTableSort = <T,>(
  rows: readonly T[],
  getValue: (row: T, key: string) => unknown,
  initialSort: TableSortState = null
) => {
  const [sort, setSort] = useState<TableSortState>(initialSort);
  const sortedRows = useMemo(
    () => sortTableRows(rows, sort, getValue),
    [getValue, rows, sort]
  );
  const onSort = useCallback((key: string) => {
    setSort((current) => ({
      key,
      direction:
        current?.key === key && current.direction === 'ascending'
          ? 'descending'
          : 'ascending'
    }));
  }, []);

  return { sortedRows, sort, onSort };
};

export const SortableHeaderButton: React.FC<{
  label: ReactNode;
  active: boolean;
  direction?: TableSortDirection;
  align?: 'left' | 'right';
  onClick: () => void;
}> = ({ label, active, direction = 'ascending', align = 'left', onClick }) => (
  <SortButton
    type="button"
    $align={align}
    aria-pressed={active}
    onClick={onClick}
  >
    <span>{label}</span>
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {active ? (
        <path
          d={
            direction === 'ascending'
              ? 'M10 15V5m0 0L6 9m4-4 4 4'
              : 'M10 5v10m0 0 4-4m-4 4-4-4'
          }
        />
      ) : (
        <path d="m6 8 4-4 4 4m-8 4 4 4 4-4" />
      )}
    </svg>
  </SortButton>
);

const SortButton = styled.button<{ $align: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'right' ? 'flex-end' : 'space-between'};
  gap: 8px;
  width: 100%;
  flex: 1 1 0;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: ${({ $align }) => $align};
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--terracotta);
    outline-offset: 2px;
  }

  span {
    min-width: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
  }
`;
